// Legal page translations
export const legalTranslations = {
  en: {
    privacy: {
      metaTitle: "Privacy Policy | Vitalo",
      metaDesc: "Learn how Vitalo collects, uses, and protects your data. We don't sell your data and give you full control to export or delete at any time.",
      title: "Privacy Policy",
      intro: "Vitalo (“we”) is a wellness companion. This policy explains what we collect, why, and the choices you have. Vitalo is not a medical device and is not intended for medical purposes. We follow the Saudi Personal Data Protection Law (PDPL) and, where they apply, the EU and UK GDPR and similar local laws.",
      sections: [
        {
          heading: "What we collect",
          body: [
            "Account details you give us (such as name, email, and profile basics).",
            "Wellness and lifestyle data you choose to connect or enter (such as activity, sleep, nutrition, supplements, and similar). Optional features may include cycle logs, files or values you upload from reports you already have, or readings you enter or sync.",
            "Insights created from that data, for wellness only.",
            "Technical data needed to run and secure the app (device, usage, diagnostics).",
            "Support messages you send us.",
            "You control what you connect. You do not have to use every feature."
          ]
        },
        {
          heading: "How we use data",
          body: [
            "To provide, secure, and improve Vitalo.",
            "To show you informational insights and reminders.",
            "To support you and to meet the law.",
            "We do not use your data to diagnose or treat. Insights are suggestions. Ask a qualified professional about your health.",
            "Optional features, including paid features and features we have not turned on for you, do not collect their extra data until you use them. Some optional features also need a specific in-app consent.",
            "Success Partners may show you offers. That does not mean we sell your health data to partners."
          ]
        },
        {
          heading: "Why this is allowed",
          body: [
            "We process your data to provide the app you asked for, with your consent where required, and to keep the service secure.",
            "Health-related data is sensitive under PDPL and, where GDPR applies, special category data. We rely on your consent and on providing the wellness service.",
            "Personalized insights are informational. They are not automated decisions with legal or similar serious effects. You can ignore them.",
            "If you live in a place with extra rights (including the EU, UK, or California), email privacy@vitaloapp.com. We do not sell your personal data."
          ]
        },
        {
          heading: "Sharing",
          body: [
            "We do not sell your personal data.",
            "We use trusted service providers under contract to host, secure, and operate Vitalo (for example cloud infrastructure, AI insights, analytics, email, and error monitoring). Providers may change. They may only use your data to provide their service to us.",
            "Apple, Google, and other apps you connect have their own privacy terms. We only receive what you allow.",
            "Success Partners do not receive your health profile from us in order to sell it. If you redeem an offer on a partner’s site, that partner is responsible for what you give them.",
            "Sharing with another person, or with a clinic or care provider, is something you choose. We do not turn that on for you. If the feature exists, we only send what you select, after a specific in-app consent for that share. You can stop it later. That share does not make Vitalo a medical device or medical advice.",
            "We may disclose data if the law requires it, or to protect rights and safety."
          ]
        },
        {
          heading: "Your control",
          body: [
            "Export / Delete: you can export or delete your data from within the app or by contacting us.",
            "Withdraw consent: for connected devices or optional processing, disconnect anytime.",
            "Access / rectify: ask us to access or correct your info.",
            "You can review and stop a share you created.",
            "You may also have rights to restrict or object to some processing, and to receive a copy of your data, under PDPL, GDPR, or similar laws."
          ]
        },
        {
          heading: "Retention",
          body: [
            "We keep data only as long as needed for the purposes above or as required by law. You can delete your account to remove most data sooner."
          ]
        },
        {
          heading: "Security",
          body: [
            "We use encryption in transit and at rest, least-privilege access, and continuous monitoring. No method is 100% secure, but we work hard to protect your data."
          ]
        },
        {
          heading: "Children",
          body: [
            "Vitalo is intended only for users 18 years or older. We do not knowingly collect data from children. If we learn that data from a minor has been collected, we will delete it promptly."
          ]
        },
        {
          heading: "Where your data is processed",
          body: [
            "Today, your data may be processed in more than one country, using trusted providers and the safeguards the law requires (including standard contractual clauses where GDPR applies).",
            "We plan to host data in the country where the app is live and/or to let you choose a region when that option is available. We will tell you in the app or this policy when you can choose. Until then, the model above applies."
          ]
        },
        {
          heading: "Changes",
          body: [
            "We may update this policy. We'll post changes here and update the \"Last updated\" date. Material changes may be notified by email or in-app."
          ]
        },
        {
          heading: "Contact",
          body: [
            "Use our contact form to reach us with any questions or concerns.",
            "Privacy-related inquiries may be sent to privacy@vitaloapp.com.",
            "Vitalo is operated by Anan Almajara Lilanzima AlMutaqadima Wataqniat Almaelumat, registered in Saudi Arabia."
          ]
        }
      ]
    },
    terms: {
      metaTitle: "Terms of Service | Vitalo",
      metaDesc: "Read the Vitalo Terms of Service. Understand the rules of using our personal wellness and lifestyle companion and your rights.",
      title: "Terms of Service",
      intro: "Welcome to Vitalo. By accessing or using our website or application, you agree to these Terms of Service.",
      sections: [
        {
          heading: "Who we are",
          body: [
            "Vitalo is operated by Anan Almajara Lilanzima AlMutaqadima Wataqniat Almaelumat, registered in Saudi Arabia. Vitalo is your AI wellness companion. It helps you view and understand lifestyle and wellness data you choose to connect or enter, and it offers informational insights for daily habits and quality of life."
          ]
        },
        {
          heading: "Intended use",
          body: [
            "Vitalo is consumer software for general wellness and lifestyle. It is not a medical device and is not intended for medical purposes. It is not intended to diagnose, prevent, monitor, treat, or cure any disease or condition, to replace a clinician, to assist conception, or to provide laboratory diagnostic information. Insights, scores, and reminders are informational only. Always consult a qualified healthcare professional before decisions about your health. Do not delay professional care because of something in Vitalo."
          ]
        },
        {
          heading: "Accounts & Eligibility",
          body: [
            "You must be 18 years or older and legally capable of forming a binding contract to use Vitalo. You are responsible for maintaining the confidentiality of your account credentials and for all activities conducted through your account."
          ]
        },
        {
          heading: "Plans and features",
          body: [
            "Vitalo may include free features, paid features, and features we show or hide by plan, country, or experiment. These Terms and the Privacy Policy apply to all of them. We only process the extra data a feature needs when you use it. Optional features stay off until you choose them. Paid plans are billed by Apple or Google under their store terms."
          ]
        },
        {
          heading: "Your Content & Permissions",
          body: [
            "You retain ownership of your data. You grant Vitalo a limited, non-exclusive license to process your data solely to provide and improve the service. You may export or delete your data at any time in accordance with our Privacy Policy."
          ]
        },
        {
          heading: "Connected Devices & Services",
          body: [
            "Vitalo may allow you to connect third-party devices or services. Such services are governed by their own terms and policies. Vitalo is not responsible for third-party content, availability, or outages."
          ]
        },
        {
          heading: "Success Partners",
          body: [
            "Success Partners is a complementary feature. It may include Partner Perks such as offers or discounts from wellness and lifestyle partners, and related partner programs. It is optional and not required to use Vitalo. We may add, change, or remove partners, offers, and this feature at our discretion. Partner products and sites have their own terms. We do not sell your health data to partners. If you use a partner offer, that partner’s own privacy terms apply to what you share with them."
          ]
        },
        {
          heading: "Sharing you choose",
          body: [
            "Optional sharing with a person you choose, or with a clinic or care provider, is off unless you give a specific in-app consent for that share. It is not required to use Vitalo. You can stop a share later. Sharing with a clinician does not make Vitalo a medical device or medical advice."
          ]
        },
        {
          heading: "Acceptable Use",
          body: [
            "You agree not to misuse Vitalo, including attempting unauthorized access, interfering with system integrity, reverse engineering, or violating applicable laws or the rights of others. We may suspend or terminate accounts for violations."
          ]
        },
        {
          heading: "Changes to the service",
          body: [
            "We may add, change, or remove features, including experimental features and Success Partners. Experimental features are provided as is. Do not rely on them for medical or high-risk decisions. We may update these Terms. We will post a new Last updated date. We will tell you in-app when a change is material. We will email only when the law requires notice of a new use or a new category of recipients."
          ]
        },
        {
          heading: "Disclaimers",
          body: [
            "Vitalo is provided \"as is\" and \"as available.\" We disclaim all warranties to the fullest extent permitted by law."
          ]
        },
        {
          heading: "Limitation of Liability",
          body: [
            "To the maximum extent permitted by law, Vitalo and its affiliates shall not be liable for indirect, incidental, consequential, or special damages, including loss of data or profits."
          ]
        },
        {
          heading: "Indemnity",
          body: [
            "You agree to indemnify and hold Vitalo harmless from claims arising out of your misuse of the service or violation of these Terms."
          ]
        },
        {
          heading: "Governing Law & Disputes",
          body: [
            "These Terms are governed by the laws of the Kingdom of Saudi Arabia, and disputes shall be subject to the exclusive jurisdiction of the competent courts in Saudi Arabia."
          ]
        },
        {
          heading: "Changes to Terms",
          body: [
            "We may update these Terms from time to time. Updates will be posted with a revised \"Last updated\" date. Material changes may be communicated by email or in-app."
          ]
        },
        {
          heading: "Contact",
          body: [
            "You may contact us through our contact form with any questions or concerns.",
            "Privacy-related inquiries may be sent to privacy@vitaloapp.com",
            "Vitalo is operated by Anan Almajara Lilanzima AlMutaqadima Wataqniat Almaelumat, registered in Saudi Arabia."
          ]
        }
      ]
    },
    ui: {
      lastUpdated: "Last updated",
      toc: "On this page"
    }
  },
  ar: {
    privacy: {
      metaTitle: "سياسة الخصوصية | فيتالو",
      metaDesc: "تعرّف على كيفية جمع واستخدام وحماية بياناتك في فيتالو. لا نبيع بياناتك ونمنحك التحكم لتصديرها أو حذفها في أي وقت.",
      title: "سياسة الخصوصية",
      intro: "فيتالو («نحن») رفيق للصحة وتعزيز جودة الحياة. توضح هذه السياسة ماذا نجمع ولماذا، وما هي خياراتك. فيتالو ليس جهازاً طبياً وغير مخصص لأغراض طبية. نلتزم بنظام حماية البيانات الشخصية في المملكة (PDPL)، وحيث ينطبق باللائحة العامة لحماية البيانات في الاتحاد الأوروبي والمملكة المتحدة (GDPR) والأنظمة المحلية المشابهة.",
      sections: [
        {
          heading: "البيانات التي نجمعها",
          body: [
            "بيانات الحساب التي تقدّمها (مثل الاسم والبريد وأساسيات الملف).",
            "بيانات الصحة وتعزيز جودة الحياة وأنماط الحياة التي تختار ربطها أو إدخالها (مثل النشاط والنوم والتغذية والمكملات وما شابه). قد تشمل الميزات الاختيارية سجلات الدورة، أو ملفات أو قيماً ترفعها من تقارير لديك، أو قراءات تدخلها أو تزامنها.",
            "تحليلات ذكية تُنشأ من تلك البيانات، لأغراض الصحة وتعزيز جودة الحياة فقط.",
            "بيانات تقنية لتشغيل التطبيق وتأمينه (الجهاز والاستخدام والتشخيص التقني).",
            "رسائل الدعم التي ترسلها.",
            "أنت تتحكم بما تربطه. ولست ملزماً باستخدام كل ميزة."
          ]
        },
        {
          heading: "كيفية استخدامنا للبيانات",
          body: [
            "لتقديم فيتالو وتأمينه وتحسينه.",
            "لعرض التحليلات الذكية والتذكيرات المعلوماتية.",
            "لدعمك وللوفاء بالنظام.",
            "لا نستخدم بياناتك للتشخيص أو العلاج. التحليلات الذكية اقتراحات. اسأل مختصاً مؤهلاً عن صحتك.",
            "الميزات الاختيارية، بما فيها المدفوعة والتي لم نفعّلها لك، لا تجمع بياناتها الإضافية حتى تستخدمها. وبعض الميزات الاختيارية تحتاج أيضاً موافقة مستقلة داخل التطبيق.",
            "قد يعرض شركاء النجاح عروضاً. هذا لا يعني أننا نبيع بياناتك الصحية للشركاء."
          ]
        },
        {
          heading: "لماذا يُسمح بذلك",
          body: [
            "نعالج بياناتك لتقديم التطبيق الذي طلبته، وبموافقتك حيث تلزم، وللحفاظ على أمان الخدمة.",
            "البيانات المرتبطة بالصحة حساسة بموجب نظام حماية البيانات الشخصية، وحيث ينطبق GDPR هي فئة خاصة. نعتمد على موافقتك وعلى تقديم خدمة الصحة وتعزيز جودة الحياة.",
            "التحليلات الذكية المخصّصة معلوماتية. وليست قرارات آلية بآثار قانونية أو آثار جدية مشابهة. يمكنك تجاهلها.",
            "إذا كنت في مكان فيه حقوق إضافية (بما في ذلك الاتحاد الأوروبي أو المملكة المتحدة أو كاليفورنيا) راسل privacy@vitaloapp.com. نحن لا نبيع بياناتك الشخصية."
          ]
        },
        {
          heading: "المشاركة",
          body: [
            "لا نبيع بياناتك الشخصية.",
            "نستخدم مزوّدين موثوقين بموجب عقود لاستضافة فيتالو وتأمينه وتشغيله (مثل البنية السحابية، والتحليلات الذكية، وتحليلات الاستخدام، والبريد، ومراقبة الأخطاء). قد يتغير المزوّدون. ولا يستخدمون بياناتك إلا لتقديم خدمتهم لنا.",
            "لتطبيقات Apple وGoogle وغيرها التي تربطها سياسات خصوصية خاصة. نستلم فقط ما تسمح به.",
            "شركاء النجاح لا يستلمون ملفك الصحي منا لبيعه. إذا استخدمت عرضاً على موقع شريك، فالشريك مسؤول عما تقدّمه له.",
            "مشاركة بياناتك مع شخص آخر أو مع عيادة أو مقدم رعاية هي اختيارك. لا نفعّلها عنك. إذا توفرت الميزة، نرسل فقط ما تحدده، وبعد موافقة مستقلة داخل التطبيق لتلك المشاركة. يمكنك إيقافها لاحقاً. هذا النوع من المشاركة لا يجعل فيتالو جهازاً طبياً ولا مشورة طبية.",
            "قد نفصح عن البيانات إذا طلب النظام ذلك، أو لحماية الحقوق والسلامة."
          ]
        },
        {
          heading: "تحكّمك",
          body: [
            "التصدير/الحذف: يمكنك تصدير بياناتك أو حذفها من داخل التطبيق أو بالتواصل معنا.",
            "سحب الموافقة: للربط الاختياري مع الأجهزة، يمكنك الإيقاف في أي وقت.",
            "الوصول/التصحيح: اطلب الوصول إلى معلوماتك أو تصحيحها.",
            "يمكنك مراجعة مشاركة أنشأتها وإيقافها.",
            "قد يكون لك أيضاً حق تقييد بعض المعالجة أو الاعتراض عليها، والحصول على نسخة من بياناتك، بموجب نظام حماية البيانات الشخصية أو GDPR أو أنظمة مشابهة."
          ]
        },
        {
          heading: "الاحتفاظ بالبيانات",
          body: [
            "نحتفظ بالبيانات للمدة اللازمة للأغراض المذكورة أو كما يقتضي القانون. يمكنك حذف الحساب لإزالة معظم البيانات مبكّرًا."
          ]
        },
        {
          heading: "الأمان",
          body: [
            "نستخدم التشفير أثناء النقل والتخزين، وصلاحيات وصول دنيا، ومراقبة مستمرة. لا توجد حماية 100٪، لكننا نبذل أقصى الجهد لحماية بياناتك."
          ]
        },
        {
          heading: "الأطفال",
          body: [
            "فيتالو مخصص فقط للمستخدمين الذين تبلغ أعمارهم 18 عامًا أو أكثر. نحن لا نجمع بيانات الأطفال عن قصد. في حال تبيّن لنا أنه تم جمع بيانات قاصر، سنقوم بحذفها فورًا."
          ]
        },
        {
          heading: "أين تُعالَج بياناتك",
          body: [
            "اليوم قد تُعالَج بياناتك في أكثر من دولة، عبر مزوّدين موثوقين وبالضمانات التي يطلبها النظام (بما في ذلك البنود التعاقدية القياسية حيث ينطبق GDPR).",
            "نخطط لاستضافة البيانات في الدولة التي يتاح فيها التطبيق و/أو إتاحة اختيار المنطقة لك عندما تتوفر هذه الخاصية. سنخبرك داخل التطبيق أو في هذه السياسة عندما يمكنك الاختيار. وحتى ذلك الحين ينطبق النموذج أعلاه."
          ]
        },
        {
          heading: "التغييرات",
          body: [
            "قد نحدّث هذه السياسة. سننشر التغييرات هنا ونحدّث تاريخ «آخر تحديث». وقد نُخطر بالتغييرات الجوهرية عبر البريد أو داخل التطبيق."
          ]
        },
        {
          heading: "التواصل",
          body: [
            "استخدم نموذج التواصل للوصول إلينا بأي أسئلة أو استفسارات.",
            "وللاستفسارات المتعلقة بالخصوصية، يمكنك التواصل معنا عبر البريد الإلكتروني: privacy@vitaloapp.com",
            "فيتالو يُدار من قبل شركة عنان المجره للأنظمة المتقدمة وتقنية المعلومات، والمسجلة في المملكة العربية السعودية."
          ]
        }
      ]
    },
    terms: {
      metaTitle: "شروط الخدمة | فيتالو",
      metaDesc: "اقرأ شروط خدمة فيتالو وتعرّف على قواعد استخدام رفيقك الشخصي للصحة وتعزيز جودة الحياة ونمط الحياة وحقوقك.",
      title: "شروط الخدمة",
      intro: "مرحبًا بك في فيتالو. باستخدامك لموقعنا الإلكتروني أو تطبيقنا، فإنك توافق على شروط الاستخدام هذه.",
      sections: [
        {
          heading: "من نحن",
          body: [
            "فيتالو يُدار من قبل شركة عنان المجره للأنظمة المتقدمة وتقنية المعلومات، والمسجلة في المملكة العربية السعودية. فيتالو رفيقك الذكي للصحة وتعزيز جودة الحياة. يساعدك على عرض بيانات نمط الحياة التي تختار ربطها أو إدخالها وفهمها، ويقدّم تحليلات ذكية معلوماتية لعاداتك اليومية وجودة حياتك."
          ]
        },
        {
          heading: "الغرض المقصود",
          body: [
            "فيتالو برنامج للمستهلك للصحة وتعزيز جودة الحياة وأنماط الحياة اليومية. ليس جهازاً طبياً وغير مخصص لأغراض طبية. وهو غير مخصص لتشخيص أي مرض أو حالة أو الوقاية منها أو مراقبتها أو علاجها أو الشفاء منها، ولا ليحل محل المختص الصحي، ولا للمساعدة على الحمل، ولا لتقديم معلومات تشخيصية مخبرية. التحليلات الذكية والمؤشرات والتذكيرات معلوماتية فقط. استشر دائماً مختصاً صحياً مؤهلاً قبل أي قرار يتعلق بصحتك. لا تؤخر الرعاية المهنية بسبب شيء في فيتالو."
          ]
        },
        {
          heading: "الحسابات والأهلية",
          body: [
            "لاستخدام فيتالو، يجب أن يكون عمرك 18 عامًا أو أكثر وأن تكون قادرًا قانونيًا على إبرام عقد ملزم. أنت مسؤول عن الحفاظ على سرية بيانات تسجيل الدخول الخاصة بك وعن جميع الأنشطة التي تتم عبر حسابك، ويجب عليك إبلاغنا فورًا في حال وجود استخدام غير مصرح به."
          ]
        },
        {
          heading: "الخطط والميزات",
          body: [
            "قد يشمل فيتالو ميزات مجانية وميزات مدفوعة وميزات نعرضها أو نخفيها حسب الخطة أو الدولة أو التجربة. تنطبق هذه الشروط وسياسة الخصوصية عليها كلها. لا نعالج البيانات الإضافية التي تحتاجها الميزة إلا عند استخدامك لها. الميزات الاختيارية تبقى مغلقة حتى تختارها. الخطط المدفوعة يفوترها Apple أو Google وفق شروط متجرهما."
          ]
        },
        {
          heading: "المحتوى والأذونات",
          body: [
            "أنت تحتفظ بملكية بياناتك. تمنح فيتالو ترخيصًا محدودًا وغير حصري لمعالجة بياناتك فقط لغرض تقديم الخدمة وتحسينها. يمكنك تصدير بياناتك أو حذفها في أي وقت وفقًا لما هو موضح في سياسة الخصوصية الخاصة بنا."
          ]
        },
        {
          heading: "الأجهزة والخدمات المتصلة",
          body: [
            "قد تتيح لك فيتالو ربط أجهزة أو خدمات تابعة لأطراف ثالثة، وتخضع هذه الخدمات لشروطها وسياساتها الخاصة. لا تتحمل فيتالو مسؤولية محتوى هذه الخدمات أو توفرها أو أي انقطاعات قد تحدث فيها."
          ]
        },
        {
          heading: "شركاء النجاح",
          body: [
            "شركاء النجاح ميزة مكمّلة. قد تشمل مزايا الشركاء مثل عروض أو خصومات من شركاء الصحة وتعزيز جودة الحياة وأنماط الحياة، وبرامج شركاء ذات صلة. هي اختيارية وليست شرطاً لاستخدام فيتالو. يجوز لنا إضافة الشركاء والعروض وهذه الميزة أو تغييرها أو إزالتها وفق تقديرنا. منتجات الشركاء ومواقعهم لها شروطها الخاصة. لا نبيع بياناتك الصحية للشركاء. إذا استخدمت عرض شريك، تنطبق سياسة خصوصيته على ما تشاركه معه."
          ]
        },
        {
          heading: "مشاركة تختارها",
          body: [
            "المشاركة الاختيارية مع شخص تختاره، أو مع عيادة أو مقدم رعاية، تبقى مغلقة ما لم تعطِ موافقة مستقلة داخل التطبيق لتلك المشاركة. ليست شرطاً لاستخدام فيتالو. يمكنك إيقاف المشاركة لاحقاً. المشاركة مع مختص صحي لا تجعل فيتالو جهازاً طبياً ولا مشورة طبية."
          ]
        },
        {
          heading: "الاستخدام المقبول",
          body: [
            "تتعهد بعدم إساءة استخدام فيتالو، بما في ذلك على سبيل المثال لا الحصر: محاولة الوصول غير المصرح به، التدخل في سلامة الأنظمة، الهندسة العكسية، إرسال الرسائل غير المرغوب فيها، أو انتهاك القوانين أو حقوق الآخرين. نحتفظ بالحق في تعليق أو إنهاء الحسابات التي تنتهك هذه الشروط."
          ]
        },
        {
          heading: "التغييرات على الخدمة",
          body: [
            "يجوز لنا إضافة ميزات أو تغييرها أو إزالتها، بما في ذلك الميزات التجريبية وشركاء النجاح. الميزات التجريبية تُقدَّم كما هي. لا تعتمد عليها في قرارات طبية أو عالية المخاطر. قد نحدّث هذه الشروط. سننشر تاريخ آخر تحديث جديد. ونبلغك داخل التطبيق عند التغيير الجوهري. ونرسل بريداً فقط عندما يطلب النظام الإشعار باستخدام جديد أو بفئة جديدة من المستلمين."
          ]
        },
        {
          heading: "إخلاء المسؤولية",
          body: [
            "يتم توفير فيتالو \"كما هو\" و\"حسب التوفر\". نخلي مسؤوليتنا عن أي ضمانات، صريحة أو ضمنية، إلى أقصى حد يسمح به النظام."
          ]
        },
        {
          heading: "تحديد المسؤولية",
          body: [
            "إلى أقصى حد يسمح به النظام، لا تتحمل فيتالو أو الشركات التابعة لها أي مسؤولية عن الأضرار غير المباشرة أو العرضية أو التبعية، بما في ذلك فقدان البيانات أو الأرباح."
          ]
        },
        {
          heading: "التعويض",
          body: [
            "توافق على تعويض فيتالو وحمايتها من أي مطالبات أو أضرار تنشأ عن إساءة استخدامك للخدمة أو مخالفتك لهذه الشروط."
          ]
        },
        {
          heading: "القانون الحاكم وتسوية النزاعات",
          body: [
            "تخضع هذه الشروط لأنظمة المملكة العربية السعودية. وتكون المحاكم المختصة في المملكة العربية السعودية هي الجهة القضائية الحصرية للنظر في أي نزاع ينشأ عن هذه الشروط أو استخدام فيتالو."
          ]
        },
        {
          heading: "التغييرات على الشروط",
          body: [
            "قد نقوم بتحديث هذه الشروط من وقت لآخر. سيتم نشر التحديثات مع تعديل تاريخ \"آخر تحديث\". قد يتم إخطارك بالتغييرات الجوهرية عبر البريد الإلكتروني أو داخل التطبيق."
          ]
        },
        {
          heading: "التواصل",
          body: [
            "يمكنك التواصل معنا عبر نموذج الاتصال الخاص بنا لأي استفسارات أو ملاحظات.",
            "وللاستفسارات المتعلقة بالخصوصية، يمكنك التواصل معنا عبر البريد الإلكتروني: privacy@vitaloapp.com",
            "فيتالو يُدار من قبل شركة عنان المجره للأنظمة المتقدمة وتقنية المعلومات، والمسجلة في المملكة العربية السعودية."
          ]
        }
      ]
    },
    ui: {
      lastUpdated: "آخر تحديث",
      toc: "على هذه الصفحة"
    }
  }
};
